import React from "react";
import { Link } from "react-router-dom";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import ActivityFeed from "./ActivityFeed";

/**
 * Community feed panel — directory cards / lists, or Activity Feed posts.
 * Post rendering is delegated to the shared ActivityFeed (not page-specific forks).
 */
function CommunityFeedPanel({
  featureType = "placeholder",
  title,
  loading = false,
  error = false,
  items = null,
  onRetry = null,
  variant = "list",
  onItemUpdate = null,
  contextType = null,
  showRankingMeta = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore = null,
}) {
  const list = Array.isArray(items) ? items : [];

  if (variant === "posts" || featureType === "social") {
    const looksLikePosts = list.some(
      (item) => item && (item.kind === "post" || item.body || item.author)
    );
    if (looksLikePosts || variant === "posts" || featureType === "social") {
      return (
        <ActivityFeed
          contextType={contextType || (featureType === "social" ? "global" : featureType)}
          title={title}
          loading={loading}
          error={error}
          items={list}
          onRetry={onRetry}
          onItemUpdate={onItemUpdate}
          showRankingMeta={showRankingMeta}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      );
    }
  }

  if (loading) {
    return (
      <div className="community-feed" role="status" aria-live="polite">
        <div className="community-feed__skeleton" />
        <div className="community-feed__skeleton" />
        <div className="community-feed__skeleton community-feed__skeleton--short" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="community-feed community-feed--error" role="alert">
        <h3 className="community-feed__heading">Couldn’t load this feed</h3>
        <p className="community-feed__body">
          Please try again in a moment. Your browse experience on Home is still
          available.
        </p>
        <div className="community-feed__actions">
          {typeof onRetry === "function" ? (
            <button type="button" className="btn btn-danger" onClick={onRetry}>
              Retry
            </button>
          ) : null}
          <Link to="/home" className="btn btn-outline-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="community-feed community-feed--empty">
        <div className="community-feed__rail" aria-hidden="true">
          <span className="community-feed__rail-dot" />
          <span className="community-feed__rail-line" />
          <span className="community-feed__rail-dot" />
          <span className="community-feed__rail-line" />
          <span className="community-feed__rail-dot" />
        </div>
        <div>
          <h3 className="community-feed__heading">
            {title || "Community activity"}
          </h3>
          <p className="community-feed__body">
            {featureType === "social"
              ? "Be the first to share an update. Create a public handle above, then post to the community feed."
              : featureType === "events"
              ? "Scheduled community events will list here with date, host, and join actions."
              : featureType === "projects"
              ? "Active community projects will show progress and related media here."
              : featureType === "organizations" || featureType === "directory"
              ? "Organization profiles will populate this directory as communities publish."
              : "Content for this community section will appear here when it launches."}
          </p>
          <div className="community-feed__actions">
            <Link to="/home" className="btn btn-danger">
              Explore Home
            </Link>
            {(featureType === "organizations" ||
              featureType === "directory" ||
              featureType === "social") && (
              <Link to="/platforms" className="btn btn-outline-secondary">
                Platform Availability
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "cards") {
    return (
      <ul className="community-feed community-feed--cards">
        {list.map((item, index) => {
          const path = item.path || item.public_path || item.href;
          const name = item.title || item.name || item.display_name;
          const image =
            item.logo_path || item.avatar || item.image || item.cover_image_path;
          const kind =
            item.kind === "creator"
              ? "Creator"
              : item.kind === "organization"
              ? "Organization"
              : item.organization_type_label || null;

          const content = (
            <>
              {image ? (
                <span className="community-feed__card-media" aria-hidden="true">
                  <img
                    src={resolveMediaUrl(image)}
                    alt=""
                    onError={handleMediaImgError}
                  />
                </span>
              ) : (
                <span
                  className="community-feed__card-media community-feed__card-media--fallback"
                  aria-hidden="true"
                >
                  {(name || "?").charAt(0).toUpperCase()}
                </span>
              )}
              <span className="community-feed__card-copy">
                {kind ? (
                  <span className="community-feed__card-kind">{kind}</span>
                ) : null}
                <span className="community-feed__card-title">{name}</span>
                {item.summary || item.description ? (
                  <span className="community-feed__card-body">
                    {item.summary || item.description}
                  </span>
                ) : null}
              </span>
            </>
          );

          return (
            <li
              key={item.id || item.slug || `community-feed-${index}`}
              className="community-feed__card"
            >
              {path ? (
                <Link to={path} className="community-feed__card-link">
                  {content}
                </Link>
              ) : (
                <div className="community-feed__card-link">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className="community-feed community-feed--list">
      {list.map((item, index) => (
        <li
          key={item.id || item.slug || `community-feed-${index}`}
          className="community-feed__item"
        >
          <h3 className="community-feed__item-title">
            {item.title || item.name}
          </h3>
          {item.summary || item.description ? (
            <p className="community-feed__item-body">
              {item.summary || item.description}
            </p>
          ) : null}
          {item.path || item.href || item.public_path ? (
            <Link
              to={item.path || item.href || item.public_path}
              className="community-feed__item-link"
            >
              View
            </Link>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export default CommunityFeedPanel;
